import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { MessageSquare, Clock, FileText, Users as UsersIcon } from "lucide-react";

const COLORS = ["#2563FF", "#18181B", "#10B981", "#F59E0B", "#8B5CF6"];

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="aria-card p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--aria-text-muted)] mb-3">
        <Icon size={14} className="text-[#2563FF]" /> {label}
      </div>
      <div className="font-serif text-3xl">{value}</div>
      {sub && <div className="text-xs text-[var(--aria-text-muted)] mt-1">{sub}</div>}
    </div>
  );
}

export default function Analytics() {
  const [summary, setSummary] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [docs, setDocs] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, b, c, d] = await Promise.all([
          api.get("/analytics/summary"),
          api.get("/analytics/questions"),
          api.get("/analytics/documents"),
          api.get("/analytics/activity"),
        ]);
        setSummary(a.data); setQuestions(b.data); setDocs(c.data); setActivity(d.data);
      } catch (err) { console.error("[ARIA]", err); }
    })();
  }, []);

  const pieData = [
    { name: "Voice", value: summary?.voice_count || 0 },
    { name: "Text", value: summary?.text_count || 0 },
  ];

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="font-serif text-3xl mb-1">Analytics</h1>
          <p className="text-sm text-[var(--aria-text-muted)]">Understand how your lab is using ARIA.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={MessageSquare} label="Questions (all time)" value={summary?.total_questions ?? "—"} sub={`${summary?.week_questions ?? 0} this week · ${summary?.month_questions ?? 0} this month`} />
          <StatCard icon={Clock} label="Avg Response" value={summary ? `${summary.avg_response_ms}ms` : "—"} sub="Across all questions" />
          <StatCard icon={FileText} label="Documents" value={summary?.documents ?? "—"} sub="Indexed in your library" />
          <StatCard icon={UsersIcon} label="Active Users" value={summary?.users ?? "—"} sub="Team members" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <div className="aria-card p-5">
            <h3 className="font-serif text-lg mb-4">Active users — last 14 days</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={activity}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip />
                <Line type="monotone" dataKey="users" stroke="#2563FF" strokeWidth={2} dot={{ fill: "#2563FF" }} />
                <Line type="monotone" dataKey="questions" stroke="#18181B" strokeWidth={2} dot={{ fill: "#18181B" }} />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="aria-card p-5">
            <h3 className="font-serif text-lg mb-4">Voice vs Text</h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={85} dataKey="value" label>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="aria-card p-5">
            <h3 className="font-serif text-lg mb-4">Most-asked questions</h3>
            <ol className="space-y-2">
              {questions.length === 0 && <li className="text-sm text-[var(--aria-text-muted)]">No data yet.</li>}
              {questions.map((q, i) => (
                <li key={i} className="flex items-start gap-3 text-sm" data-testid={`top-q-${i}`}>
                  <span className="font-serif text-[#2563FF] w-6">{i+1}.</span>
                  <span className="flex-1">{q.query}</span>
                  <span className="text-xs bg-[#2563FF]/10 text-[#2563FF] px-2 py-0.5 rounded">{q.count}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="aria-card p-5">
            <h3 className="font-serif text-lg mb-4">Most-searched documents</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={docs} layout="vertical">
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={11} width={140} />
                <Tooltip />
                <Bar dataKey="count" fill="#2563FF" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
